import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { authService } from '../services/authService';
import { supabase } from '../lib/supabaseClient';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  isPremium: boolean;
  isTester: boolean;
  isAdmin: boolean;
  joinDate: string;
  bio?: string;
  avatarUrl?: string;
  targetBandScore?: number;
  studyTimeline?: string;
  premiumExpiryDate?: string;
  stats: {
    totalEssays: number;
    averageScore: number;
    currentStreak: number;
    weeklyGoal: number;
    completedGoals: number;
  };
}

interface UserContextType {
  user: User | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

const normalizeUser = (value: User | null): User | null => {
  if (!value) return null;
  return {
    ...value,
    isTester: Boolean(value.isTester),
    isAdmin: Boolean(value.isAdmin),
  };
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem('user_data');
      return cached ? normalizeUser(JSON.parse(cached)) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Check for existing session on app load and listen to auth changes
  useEffect(() => {
    const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
      return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('AUTH_CHECK_TIMEOUT')), timeoutMs);
        promise
          .then((value) => {
            clearTimeout(timer);
            resolve(value);
          })
          .catch((error) => {
            clearTimeout(timer);
            reject(error);
          });
      });
    };

    const checkAuthStatus = async () => {
      try {
        // Get current user from Supabase
        const userData = await withTimeout(authService.getCurrentUser(), 6000);
        
        if (userData) {
          setUser(normalizeUser(userData));
        } else {
          // Avoid dropping an already authenticated in-memory user on a transient null probe.
          setUser(prev => prev ?? null);
        }
      } catch (error) {
        // Timeout is handled as a soft-failure to avoid loading lock.
        if (!(error instanceof Error && error.message === 'AUTH_CHECK_TIMEOUT')) {
          console.error('Error checking auth status:', error);
        }
        // Keep current user state on transient auth check failures.
        setUser(prev => prev ?? null);
      } finally {
        setIsLoading(false);
      }
    };

    // Check auth status on mount
    setIsLoading(true);
    checkAuthStatus();

    // Listen to Supabase auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // User signed in
        const userData = await authService.getCurrentUser();
        if (userData) {
          setUser(normalizeUser(userData));
        }
      } else if (event === 'SIGNED_OUT') {
        // User signed out
        setUser(null);
        localStorage.removeItem('user_data');
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Token refreshed, update user data
        const userData = await authService.getCurrentUser();
        if (userData) {
          setUser(normalizeUser(userData));
        }
      } else if (event === 'USER_UPDATED' && session?.user) {
        // User data updated
        const userData = await authService.getCurrentUser();
        if (userData) {
          setUser(normalizeUser(userData));
        }
      }
    });

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = (userData: User) => {
    setUser(normalizeUser(userData));
  };

  const logout = async () => {
    // Clear UI state immediately so logout feels instant even if network is slow.
    setUser(null);
    localStorage.removeItem('user_data');
    void authService.logout();
  };

  const updateUser = (userData: Partial<User>) => {
    setUser(prev => prev ? { ...prev, ...userData } : null);
  };


  // isLoggedIn is simply whether we have a user
  const isLoggedIn = user !== null;

  const value: UserContextType = {
    user,
    isLoggedIn,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
